/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = this.querySelectorAll('.slider__item');
        this.progressBarFill = this.querySelector('.card__progress-bar-fill');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
        this.carouselActive = false;
        this.hoverLeaveTimeout = null;
        this.carouselFadeTimeout = null;
        this.isDesktopHoverMode = false;
      }

      /**
       * Activates a deferred slide by swapping data-src/data-srcset to src/srcset.
       * No-op if the slide doesn't exist or is already loaded.
       * @param {number} index - Slide index.
       */
      hydrateSlide(index) {
        const slide = this.slides[index];
        if (!slide) return;
        slide.querySelectorAll('[data-src]').forEach((el) => {
          if (el.dataset.srcset) {
            el.setAttribute('srcset', el.dataset.srcset);
            el.removeAttribute('data-srcset');
          }
          el.setAttribute('src', el.dataset.src);
          el.removeAttribute('data-src');
        });
      }

      /**
       * Whether desktop hover carousel mode is enabled.
       * @returns {boolean}
       */
      checkDesktopHoverMode() {
        return window.matchMedia('(min-width: 769px)').matches
          && this.slides.length > 1
          && !!this.featuredImage;
      }

      init() {
        super.init();
        this.featuredImage = this.querySelector('.card__featured-image');
        this.isDesktopHoverMode = this.checkDesktopHoverMode();

        this.hydrateSlide(1);

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        if (this.progressBarFill) {
          this.slider.addEventListener('scroll', this.updateProgressBar.bind(this));
          this.updateProgressBar();
        }

        if (this.isDesktopHoverMode) {
          this.slider.scrollTo({ left: 0, behavior: 'instant' });
        }
        this.setupDesktopHoverListeners();

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slides[1]?.setAttribute('aria-current', 'true');
          }
        }
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.setButtonStates();
      }

      /**
       * Syncs the static featured image with a carousel slide.
       * @param {Element} slide - Slide element to copy image from.
       */
      updateFeaturedImageFromSlide(slide) {
        if (!this.featuredImage || !slide) return;

        const srcImg = slide.querySelector('img');
        const destImg = this.featuredImage.querySelector('img');
        if (!srcImg || !destImg) return;

        if (srcImg.dataset.src) {
          this.hydrateSlide([...this.slides].indexOf(slide));
        }

        destImg.src = srcImg.currentSrc || srcImg.src;
        if (srcImg.srcset) destImg.srcset = srcImg.srcset;
        this.featuredImage.dataset.mediaId = slide.dataset.mediaId;
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          const targetSlide = this.querySelector(`.slider__item[data-media-id="${swatchMediaId}"]`);
          if (targetSlide) {
            const idx = [...this.slides].indexOf(targetSlide);
            this.hydrateSlide(idx);
            this.hydrateSlide(idx + 1);
          }

          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const slideEl = variantMedia.closest('.slider__item');

            if (this.isDesktopHoverMode && !this.carouselActive) {
              this.slider.scrollTo({ left: 0, behavior: 'instant' });
              this.updateFeaturedImageFromSlide(slideEl);
            } else {
              const left = slideEl.offsetLeft;
              this.slider.scrollTo({ left, behavior: 'instant' });
            }
          }

          requestAnimationFrame(() => this.updateProgressBar());
        }
      }

      /**
       * Returns the default carousel index on desktop hover (slide 2).
       * @returns {number}
       */
      getDefaultCarouselIndex() {
        if (this.slides[1] && !this.slides[1].hidden) return 1;

        for (let i = 1; i < this.slides.length; i += 1) {
          if (!this.slides[i].hidden) return i;
        }

        return 0;
      }

      /**
       * Sets the disabled state of the nav buttons.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan) + 1;
        const visibleSlideCount = this.slides.filter((slide) => slide.hidden !== true).length;

        this.prevBtn.disabled = currentSlideIndex === 1
          || (this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0);
        this.nextBtn.disabled = visibleSlideCount === currentSlideIndex
          || this.getSlideVisibility(this.slides[this.slides.length - 1]);
      }

      /**
       * Updates the progress bar fill width and position based on scroll state.
       */
      updateProgressBar() {
        if (!this.progressBarFill) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.slider;
        const maxScroll = scrollWidth - clientWidth;
        if (maxScroll <= 0) {
          this.progressBarFill.style.width = '100%';
          this.progressBarFill.style.left = '0';
          return;
        }
        const fillWidth = (clientWidth / scrollWidth) * 100;
        const scrollProgress = scrollLeft / maxScroll;
        const maxOffset = 100 - fillWidth;
        this.progressBarFill.style.width = `${fillWidth}%`;
        this.progressBarFill.style.left = `${scrollProgress * maxOffset}%`;
      }

      /**
       * Handles raw 'scroll' events on the slider element.
       * Hydrates upcoming slides on demand as the user swipes.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
        if (this.slideSpan) {
          const idx = Math.round(this.slider.scrollLeft / this.slideSpan);
          this.hydrateSlide(idx);
          this.hydrateSlide(idx + 1);
        }
      }

      /**
       * Binds or unbinds desktop hover listeners based on current breakpoint.
       */
      setupDesktopHoverListeners() {
        if (!this.boundHandleHoverIn) {
          this.boundHandleHoverIn = this.handleHoverIn.bind(this);
          this.boundHandleHoverOut = this.handleHoverOut.bind(this);
        }

        this.removeEventListener('mouseenter', this.boundHandleHoverIn);
        this.removeEventListener('mouseleave', this.boundHandleHoverOut);

        if (this.isDesktopHoverMode) {
          this.addEventListener('mouseenter', this.boundHandleHoverIn);
          this.addEventListener('mouseleave', this.boundHandleHoverOut);
        }
      }

      /**
       * Clears all pending hover/carousel transition timers.
       */
      clearCarouselTimers() {
        clearTimeout(this.hoverLeaveTimeout);
        clearTimeout(this.carouselFadeTimeout);
      }

      /**
       * Fades in the carousel on desktop hover, starting at slide 2.
       */
      activateCarousel() {
        const targetIndex = this.getDefaultCarouselIndex();
        this.hydrateSlide(targetIndex);
        this.hydrateSlide(targetIndex + 1);
        this.slider.scrollTo({ left: targetIndex * this.slideSpan, behavior: 'instant' });

        this.carouselActive = true;
        this.classList.add('is-carousel-active');
        // Force reflow so the opacity transition runs reliably on fast hovers.
        void this.offsetWidth;
        this.classList.add('is-carousel-active--visible');
        this.setButtonStates();
      }

      /**
       * Completes carousel deactivation and restores the featured image.
       */
      finishDeactivateCarousel() {
        this.carouselActive = false;
        this.classList.remove('is-carousel-active', 'is-carousel-active--visible', 'is-carousel-active--leaving');
        this.slider.scrollTo({ left: 0, behavior: 'instant' });
        this.setButtonStates();
      }

      /**
       * Starts the carousel fade-out animation.
       */
      startCarouselFadeOut() {
        clearTimeout(this.carouselFadeTimeout);
        if (!this.carouselActive) return;

        this.classList.remove('is-carousel-active--visible');
        this.classList.add('is-carousel-active--leaving');
        this.carouselFadeTimeout = setTimeout(() => {
          if (this.matches(':hover')) {
            this.classList.remove('is-carousel-active--leaving');
            this.classList.add('is-carousel-active--visible');
            return;
          }
          this.finishDeactivateCarousel();
        }, 400);
      }

      /**
       * Resets carousel to the initial featured-image state.
       * @param {boolean} animate - Whether to fade out before resetting.
       */
      deactivateCarousel(animate = true) {
        this.clearCarouselTimers();

        if (!animate) {
          this.finishDeactivateCarousel();
          return;
        }

        this.startCarouselFadeOut();
      }

      /**
       * On desktop hover: fade in carousel at slide 2.
       */
      handleHoverIn() {
        if (!this.isDesktopHoverMode) return;

        this.clearCarouselTimers();
        this.classList.remove('is-carousel-active--leaving');

        if (this.carouselActive) {
          this.classList.add('is-carousel-active', 'is-carousel-active--visible');
          return;
        }

        this.activateCarousel();
      }

      /**
       * On desktop mouse-out: fade out carousel and restore featured image.
       */
      handleHoverOut() {
        if (!this.isDesktopHoverMode || !this.carouselActive) return;

        clearTimeout(this.hoverLeaveTimeout);
        this.hoverLeaveTimeout = setTimeout(() => {
          if (this.matches(':hover')) return;
          this.startCarouselFadeOut();
        }, 80);
      }

      /**
       * Handles 'on:breakpoint-change' events on the window.
       */
      handleBreakpointChange() {
        const wasDesktopHoverMode = this.isDesktopHoverMode;
        if (this.nav) this.removeListeners();
        this.initSlider();

        this.isDesktopHoverMode = this.checkDesktopHoverMode();
        this.setupDesktopHoverListeners();

        if (this.carouselActive && !this.isDesktopHoverMode) {
          this.deactivateCarousel(false);
        } else if (this.isDesktopHoverMode && !wasDesktopHoverMode) {
          this.slider.scrollTo({ left: 0, behavior: 'instant' });
        }
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      handleScroll() {
        super.handleScroll();
        this.slides[this.currentIndex].setAttribute('aria-current', 'true');
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
